<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import OpportunityCustomFolderSection from './OpportunityCustomFolderSection.vue';

const props = defineProps({
  card: { type: Object, required: true },
  attributes: { type: Array, default: () => [] },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const store = useStore();

const UTM_FIELDS = [
  { key: 'utm_source', label: 'Fonte', hint: 'UTM Source' },
  { key: 'utm_campaign', label: 'Campanha', hint: 'UTM Campaign' },
  { key: 'utm_medium', label: 'Conjunto', hint: 'UTM Medium' },
  { key: 'utm_content', label: 'Criativo', hint: 'UTM Content' },
  { key: 'utm_term', label: 'Formulário', hint: 'UTM Term' },
];

const utmValues = ref({});
const editingKey = ref(null);
const textareaRef = ref(null);

async function startEditing(key) {
  editingKey.value = key;
  await nextTick();
  if (textareaRef.value) {
    textareaRef.value.focus();
    const range = document.createRange();
    const sel = window.getSelection();
    range.selectNodeContents(textareaRef.value);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

watch(
  () => props.card.custom_attributes,
  attrs => {
    UTM_FIELDS.forEach(f => {
      // Não sobrescreve campo que está sendo editado no momento
      if (editingKey.value === f.key) return;
      utmValues.value[f.key] = attrs?.[f.key] || '';
    });
  },
  { immediate: true }
);

const UTM_KEYS = UTM_FIELDS.map(f => f.key);

const extraAttributes = computed(() =>
  props.attributes.filter(
    a => a.attribute_group === 'opp_utm' && !UTM_KEYS.includes(a.attribute_key)
  )
);

const customAttributes = computed(() => props.card.custom_attributes || {});

async function saveUtmField(key, event) {
  const savedValue =
    event?.target?.innerText?.trim() ??
    textareaRef.value?.innerText?.trim() ??
    utmValues.value[key];
  editingKey.value = null;
  utmValues.value[key] = savedValue;
  const updatedCustomAttributes = {
    ...props.card.custom_attributes,
    [key]: savedValue,
  };
  await store.dispatch('kanban/updateCard', {
    cardId: props.card.id,
    data: { custom_attributes: updatedCustomAttributes },
  });
  emit('update', updatedCustomAttributes);
}
</script>

<template>
  <div class="space-y-6">
    <h4 class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider">
      {{ t('KANBAN.OPPORTUNITY_MODAL.NAV_UTM') }}
    </h4>
    <div>
      <div class="flex flex-col gap-3">
        <div
          v-for="field in UTM_FIELDS"
          :key="field.key"
          class="flex flex-col gap-1"
        >
          <div class="relative group/desc">
            <label class="text-xs text-n-slate-10 cursor-default">{{
              field.label
            }}</label>
            <div
              class="absolute left-0 bottom-5 z-20 hidden group-hover/desc:block bg-n-surface-2 border border-n-weak text-n-slate-12 text-xs rounded px-2.5 py-1.5 whitespace-nowrap shadow-xl pointer-events-none"
            >
              {{ field.hint }}
            </div>
          </div>
          <div
            v-if="editingKey === field.key"
            ref="textareaRef"
            contenteditable="true"
            class="text-sm text-left px-2 py-1 rounded bg-n-alpha-2 text-n-slate-12 min-h-[30px] w-full outline-none break-all"
            style="box-shadow: inset 0 -1px 0 0 #2781f6"
            @blur="saveUtmField(field.key, $event)"
            @keydown.enter.prevent="saveUtmField(field.key, $event)"
            @keydown.esc="editingKey = null"
          >
            {{ utmValues[field.key] }}
          </div>
          <button
            v-else
            class="group/utm text-sm text-left px-2 py-1 rounded bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 min-h-[30px] w-full"
            :class="{ 'text-n-slate-9': !utmValues[field.key] }"
            @click="startEditing(field.key)"
          >
            <span class="block truncate group-hover/utm:hidden">{{
              utmValues[field.key] || '—'
            }}</span>
            <span
              class="hidden group-hover/utm:block break-all whitespace-pre-wrap"
              >{{ utmValues[field.key] || '—' }}</span>
          </button>
        </div>
      </div>
    </div>

    <div v-if="extraAttributes.length">
      <h4
        class="text-xs font-semibold text-n-slate-10 uppercase tracking-wide mb-3"
      >
        {{ t('KANBAN.UTM.EXTRA_TITLE') }}
      </h4>
      <OpportunityCustomFolderSection
        :attributes="extraAttributes"
        :custom-attributes="customAttributes"
        :card-id="card.id"
        @update="emit('update', $event)"
      />
    </div>
  </div>
</template>
